import { Inject, Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { NoteBlobReq, S3NoteBlobsData, S3NoteBlobsByField, NoteBlobAction, NoteBlobKeyObject } from "@app/models/noteblob.model";
import { APP_BASE_HREF } from "@angular/common";
import { Note, NoteUpdate } from "@app/models/note.model";
import { PictureDTO } from "@care-connect-japan/ckweblib/lib/data/schema/data-model/dto/picture";
import { from, map, Observable, of } from "rxjs";
import { TitleModel } from "@app/store/title/title.state";
import { xmlToJson } from "@app/shared/common/utils";
import { StorageEnum } from "@app/shared/enum/storage";

@Injectable({
  providedIn: 'root',
})
export class NoteBlobService {

  constructor(
    @Inject(APP_BASE_HREF) private baseHref: string,
    private apiService: ApiService) { }

  readonly WGT_PICTURE: string = '8';

  get orgcd(): string {
    return localStorage.getItem(StorageEnum.CORPPREFIX) ?? this.baseHref;
  }

  public checkForNoteBlobInTitle(title: TitleModel): boolean {
    if (!title || !title.ttlstruct) return false
    let data = xmlToJson(title.ttlstruct);
    let itms = (data[0]?.children as any[]).flat();
    return itms.some(x => x.wtype == this.WGT_PICTURE);

  }

  async createPresign(payload: NoteBlobReq): Promise<S3NoteBlobsData | null> {
    if (!payload.orgId || payload.orgId == "") {
      payload.orgId = this.orgcd;
    }
    return await this.apiService.createPresignUrl(payload) || null;
  }

  public fetchAndMapNoteBlobs(notes: Note[]): Observable<Note>[] {
    const updatedNotes$ = notes.map(note => this.fetchAndMapNoteBlob(note));
    return updatedNotes$;
  }

  public fetchAndMapNoteBlob(note: Note | any): Observable<Note> {
    if (!note?.noteid || !note?.objcd) {
      return of(note);
    }
    const payload: NoteBlobReq = {
      noteid: note.noteid.toString(),
      objkbn: note.objkbn,
      objcd: note.objcd,
      orgId: this.orgcd
    };

    return from(
      this.createPresigns(payload)).pipe(
        map((noteblobs: S3NoteBlobsByField[] | null) => {
          note.noteblobs = noteblobs?.map(x => { return { id: x.fieldid, link: x.preSignURL, key: x.key } });
          return note;
        })
      );
  }

  async createPresigns(payload: NoteBlobReq): Promise<S3NoteBlobsByField[] | null> {
    if (!payload.noteid || payload.noteid == "") return null;
    if (!payload.orgId || payload.orgId == "") {
      payload.orgId = this.orgcd;
    }
    return await this.apiService.setNoteBlobs(payload) || null;
  }

  async putDatatoS3(s3Presign: S3NoteBlobsByField[], data: PictureDTO[]) {
    for (let s3 of s3Presign) {
      if (s3.preSignURL) {
        const noteblobFile = data.find(x => x.id?.toString() === s3.fieldid)
        await this.apiService.putByPresignUrl(s3.preSignURL, noteblobFile?.file);
      }
    }

  }

  createNoteBlobS3Payload(objkbn: string, objcd: string, fieldids: NoteBlobKeyObject[], noteid?: string | undefined): NoteBlobReq {
    const corpPrefix = this.baseHref.slice(1);
    const payload: NoteBlobReq = {
      orgId: corpPrefix,
      objkbn,
      objcd,
      noteid,
      fieldids
    }
    return payload;
  }

  public async createNoteBlobPayload(payload: NoteUpdate[], isDuplicate: boolean = false) {
    let noteDatas = payload.filter(x => (x.note as any).noteblobs && (x.note as any).noteblobs);
    if (noteDatas.length === 0) {
      return payload.map(x => { delete x.note.noteblobs; return x; });
    }
    for (let noteData of noteDatas) {
      const { note } = noteData;
      const fieldids = ((note as any).noteblobs as PictureDTO[]).map(x => {
        if (x.file && x.id) {
          return {
            fieldId: x.id.toString(),
            ext: x.ext.replace('.', '')
          }
        }
        return null;
      }).filter(x => x) as NoteBlobKeyObject[];
      const duplicates = isDuplicate ? ((note as any).noteblobs as PictureDTO[]).filter(x => x.link && !x.originalData) : [];
      if (note.delstat != 1 && fieldids?.length > 0) {
        const { objkbn, objcd } = note;
        let noteLinks: S3NoteBlobsByField[] = [];
        const noteId: string | undefined = note.noteid?.toString() || 'noteid_temp_' + new Date().getTime().toString();
        const payload = this.createNoteBlobS3Payload(objkbn, objcd, fieldids, noteId);
        const urls = await this.createPresign(payload);
        if (urls?.data) {
          await this.putDatatoS3(urls.data, (note as any).noteblobs);
          noteLinks = [...urls.data.map(x => { delete x.preSignURL; return x })];
        }
        noteData.noteLinks = noteLinks;
      }
      const deleteBlobs: S3NoteBlobsByField[] = (note as any).noteblobs.filter((x: PictureDTO) => x.originalData)?.map((x: any) => {
        return { type: NoteBlobAction.Delete, key: x.originalData.key, fieldid: x.originalData.id }
      });
      noteData.noteLinks = [...deleteBlobs, ...(noteData.noteLinks || [])];
      if (note.delstat === 1 && (note as any).noteblobs.length > 0) {
        noteData.removeByNoteBlobs = (note as any).noteblobs.map((noteblob: any) => noteblob.key);
      }
      delete (note as any).noteblobs;
      if (duplicates.length) {
        // handle duplicate
        const duplicateLinks: S3NoteBlobsByField[] = duplicates.map(x => {
          let key = (x as any)?.key as string || '';
          const data: S3NoteBlobsByField = {
            fieldid: x.id!.toString(),
            preSignURL: x.link,
            key,
            type: NoteBlobAction.Update,
          };
          return data;
        }).filter(x => x);
        noteData.noteLinks = [...noteData.noteLinks, ...duplicateLinks];
      }
    }
    return payload;
  }
}